/**
 * Доска задач разработки (03.11.2026): задачи чатов Claude и людей команды портала. Видно, кто что взял и когда;
 * взять можно только свободную задачу — чужую уже взятую портал не отдаёт. Открыта владельцу портала и людям,
 * которых он добавил; чаты работают с ней ключом через `scripts/board.mjs`.
 * Вкладка, отбор и открытая задача — в адресе страницы.
 */
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { ApiError } from '../../lib/api';
import { Button } from '../../ui/button';
import { EmptyState, ErrorBox, Spinner } from '../../ui/feedback';
import { Page, PageHeader } from '../../ui/layout';
import { Tabs } from '../psd/shared';
import { AccessTab } from './AccessTab';
import { boardMeQuery } from './access';
import { ChatsTab } from './ChatsTab';
import { NewTaskDialog } from './NewTaskDialog';
import { BOARD_TAB_TITLES, type BoardSearch, type BoardTab } from './shared';
import { TaskSheet } from './TaskSheet';
import { TasksTab } from './TasksTab';

export function DevBoardPage() {
  const search = useSearch({ strict: false }) as BoardSearch;
  const navigate = useNavigate();
  const tab: BoardTab = search.tab ?? 'tasks';
  const setSearch = useCallback(
    (patch: Partial<BoardSearch>) =>
      navigate({ to: '/devboard', search: (prev: BoardSearch) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const me = useQuery(boardMeQuery);
  const [creating, setCreating] = useState(false);
  useCrumbs(tab === 'tasks' ? null : [{ label: BOARD_TAB_TITLES[tab] }]);
  const openTask = (id: string) =>
    navigate({ to: '/devboard', search: (prev: BoardSearch) => ({ ...prev, task: id }) });

  if (me.isPending)
    return (
      <Page>
        <Spinner />
      </Page>
    );
  if (me.isError || !me.data)
    return (
      <Page>
        <PageHeader title="Доска задач разработки" />
        {me.error instanceof ApiError && me.error.status === 403 ? (
          <EmptyState title="Доска закрыта">
            Доска задач разработки открыта владельцу портала и людям, которых он добавил. Нужен доступ —
            напишите владельцу портала.
          </EmptyState>
        ) : (
          <ErrorBox error={me.error} />
        )}
      </Page>
    );
  const board = me.data;
  return (
    <Page>
      <PageHeader
        title="Доска задач разработки"
        subtitle="Задачи чатов Claude и команды портала: кто что взял и когда. Взять можно только свободную задачу — взятую другим портал не отдаст."
        actions={
          <Button
            variant="primary"
            size="lg"
            icon={<Plus className="size-4" />}
            onClick={() => setCreating(true)}
          >
            Новая задача
          </Button>
        }
      />
      <Tabs
        value={tab}
        onChange={(v) => setSearch({ tab: v === 'tasks' ? undefined : v, task: undefined })}
        items={(Object.keys(BOARD_TAB_TITLES) as BoardTab[]).map((v) => ({
          value: v,
          label: BOARD_TAB_TITLES[v],
        }))}
      />
      {tab === 'tasks' ? (
        <TasksTab me={board} search={search} setSearch={setSearch} onOpen={openTask} />
      ) : tab === 'chats' ? (
        <ChatsTab onChat={(code) => setSearch({ tab: undefined, chat: code })} />
      ) : (
        <AccessTab me={board} />
      )}
      <TaskSheet id={search.task} me={board} onClose={() => setSearch({ task: undefined })} />
      <NewTaskDialog
        open={creating}
        onOpenChange={setCreating}
        defaultChat={search.chat}
        onCreated={(id) => setSearch({ task: id })}
      />
    </Page>
  );
}
